import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Share2, Copy, Eye, Trash2, RefreshCw, ExternalLink, FileDown } from "lucide-react";
import { sharingService } from "@/services/sharing/sharingService";
import { SharedResultLink } from "@/types/sharing/sharing.types";
import { ExportPdfDialog, PdfExportMethod } from "./ExportPdfDialog";
import { pdfExportService } from "@/services/pdf/pdfExportService";
import { fullPagePdfService } from "@/services/pdf/fullPagePdfService";
import { PdfExportData, PdfCarbonData, PdfHydricData, PdfTranslations } from "@/types/pdf/pdf.types";
import { useData } from "@/hooks/useData";
import { FootprintDocument, FootprintProduct, FootprintConsumption } from "@/types/results";
import { Company } from "@/types/company/company.types";
import enTranslations from "@/locales/en.json";
import ptTranslations from "@/locales/pt.json";

// Helper to get translation for a specific language
const getTranslation = (language: 'en' | 'pt', key: string): string => {
  const translations = language === 'pt' ? ptTranslations : enTranslations;
  const keys = key.split('.');
  let value: any = translations;
  for (const k of keys) {
    if (value === undefined) return key;
    value = value[k];
  }
  return value || key;
};

interface SharingSectionProps {
  companyId: string;
  companyName: string;
  productId: string;
  productName: string;
  year: number;
  footprintType: 'carbon' | 'hydric' | 'both';
}

export const SharingSection = ({
  companyId,
  companyName,
  productId,
  productName,
  year,
  footprintType
}: SharingSectionProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [sharedLinks, setSharedLinks] = useState<SharedResultLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [selectedLinkForPdf, setSelectedLinkForPdf] = useState<SharedResultLink | null>(null);
  const [pdfDataLoading, setPdfDataLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Fetch footprint data
  const { data: footprintDocuments } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  const { data: footprintConsumptions } = useData<FootprintConsumption>({
    collectionName: "footprint_consumptions",
  });

  const { data: companies } = useData<Company>({
    collectionName: "companies",
  });

  // Load existing shared links for this company
  useEffect(() => {
    const loadSharedLinks = async () => {
      setIsLoading(true);
      try {
        const links = await sharingService.getCompanySharedLinks(companyId);
        setSharedLinks(links);
      } catch (error) {
        console.error('Error loading shared links:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (companyId) {
      loadSharedLinks();
    }
  }, [companyId]);

  const generateShareLink = async () => {
    if (!user?.uid) return;

    setIsGenerating(true);
    try {
      await sharingService.generateShareLink({
        companyId,
        companyName,
        productId,
        productName,
        year,
        footprintType,
        userId: user.uid
      });

      const updatedLinks = await sharingService.getCompanySharedLinks(companyId);
      setSharedLinks(updatedLinks);

      toast({
        title: t("results.sharing.success.linkGenerated"),
        description: t("results.sharing.descriptions.linkGenerated"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("results.sharing.error.generateFailed"),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (token: string) => {
    const shareUrl = `${window.location.origin}/shared/${token}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: t("results.sharing.success.linkCopied"),
        description: t("results.sharing.descriptions.linkCopied"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("results.sharing.error.copyFailed"),
        variant: "destructive",
      });
    }
  };

  const toggleLinkStatus = async (linkId: string, isActive: boolean) => {
    try {
      if (isActive) {
        await sharingService.deactivateLink(linkId);
      } else {
        await sharingService.reactivateLink(linkId);
      }

      setSharedLinks(prev =>
        prev.map(link =>
          link.id === linkId
            ? { ...link, isActive: !isActive }
            : link
        )
      );

      toast({
        title: isActive ? t("results.sharing.success.linkDeactivated") : t("results.sharing.success.linkReactivated"),
        description: isActive ? t("results.sharing.descriptions.linkDeactivated") : t("results.sharing.descriptions.linkReactivated"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("results.sharing.error.toggleFailed"),
        variant: "destructive",
      });
    }
  };

  const deleteLink = async (linkId: string) => {
    try {
      await sharingService.deleteLink(linkId);
      setSharedLinks(prev => prev.filter(link => link.id !== linkId));

      toast({
        title: t("results.sharing.success.linkDeleted"),
        description: t("results.sharing.descriptions.linkDeleted"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        description: t("results.sharing.error.deleteFailed"),
        variant: "destructive",
      });
    }
  };

  const openSharedLink = (token: string) => {
    const shareUrl = `${window.location.origin}/shared/${token}`;
    window.open(shareUrl, '_blank');
  };

  /**
   * Build PDF export data from footprint data
   */
  const buildPdfExportData = useCallback((link: SharedResultLink, language: 'en' | 'pt'): PdfExportData | null => {
    // Find the footprint document for this company and year
    const footprintDoc = footprintDocuments?.find(
      (fp) => fp.company_id === link.companyId && fp.year === link.year
    );

    if (!footprintDoc) {
      console.error('No footprint document found');
      return null;
    }

    // Find the product
    const product = footprintProducts?.find(
      (p) => p.footprint_id === footprintDoc.id && p.name === link.productName
    );

    if (!product) {
      console.error('No product found');
      return null;
    }

    // Get related consumptions
    const consumptions = footprintConsumptions?.filter(
      (c) => c.footprint_id === footprintDoc.id
    ) || [];

    // Get company logo
    const company = companies?.find((c) => c.id === link.companyId);

    // Get material consumptions for breakdown
    const materialConsumptions = consumptions.filter((c) => c.consumption_type === "material");

    // Get activity type
    const activityType = product.product_flow || footprintDoc.general_data?.activityType || "extraction";

    // Calculate decision ratio
    const generalData = footprintDoc.general_data;
    let decisionRatio = 1;
    if (generalData) {
      if (activityType === "extraction") {
        const pvpCaro = Number(generalData.pvp_caro_ext) || 0;
        const pvpBarato = Number(generalData.pvp_barato_ext) || 0;
        decisionRatio = pvpBarato > 0 ? pvpCaro / pvpBarato : 1;
      } else if (activityType === "transformation") {
        const pvpCaro = Number((generalData as any).pvp_caro_trsf) || 0;
        const pvpBarato = Number((generalData as any).pvp_barato_trsf) || 0;
        decisionRatio = pvpBarato > 0 ? pvpCaro / pvpBarato : 1;
      }
    }

    const isPhysicalAllocation = decisionRatio <= 5;
    const allocationMethod = isPhysicalAllocation ? 'physical' : 'economic';

    // Calculate consumption breakdowns
    const materialsCarbon = consumptions
      .filter((c) => c.consumption_type === "material")
      .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);
    const electricityCarbon = consumptions
      .filter((c) => c.consumption_type === "electricity")
      .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);
    const fuelsCarbon = consumptions
      .filter((c) => c.consumption_type === "fuels")
      .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);
    const residueCarbon = consumptions
      .filter((c) => c.consumption_type === "residue")
      .reduce((sum, c) => sum + (c.carbon_footprint || 0), 0);

    // Build carbon data
    let carbonData: PdfCarbonData | undefined;
    if (link.footprintType === 'carbon' || link.footprintType === 'both') {
      const totalCarbon = (product.carbon_footprint_total || 0) * (product.annual_production || 0);
      
      // Get product flow to determine which phases to show
      const productFlow = product.product_flow || activityType;

      // Calculate extraction phase values
      let pc_extraction = undefined;
      let pc_ext_materials = undefined;
      let pc_ext_ecr = undefined;

      if (productFlow !== "transformation") {
        // Extraction products
        pc_ext_materials = (product.carbon_footprint_materials || materialsCarbon) * (product.annual_production || 0);
        pc_ext_ecr = (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon) * (product.annual_production || 0);
      } else {
        // Transformation products
        if (product.carbon_footprint_extraction && product.carbon_footprint_extraction > 0) {
          pc_extraction = product.carbon_footprint_extraction * (product.annual_production || 0);
        }
      }
      const pc_trans = productFlow !== "extraction"
        ? (product.carbon_footprint_transport || 0) * (product.annual_production || 0)
        : undefined;
      const pc_trsf = productFlow !== "extraction"
        ? ((product.carbon_footprint_materials || materialsCarbon) + 
           (product.carbon_footprint_electricity || electricityCarbon + fuelsCarbon + residueCarbon)) * (product.annual_production || 0)
        : undefined;

      // Build phases based on product flow
      const phases = [];
      
      if (productFlow === "extraction") {
        const totalDisplayedComponents = (pc_ext_materials || 0) + (pc_ext_ecr || 0);
        
        if (pc_ext_materials && pc_ext_materials > 0) {
          phases.push({
            name: getTranslation(language, 'results.carbon.extraction.materials'),
            value: pc_ext_materials,
            percentage: totalDisplayedComponents > 0 ? (pc_ext_materials / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (pc_ext_ecr && pc_ext_ecr > 0) {
          phases.push({
            name: getTranslation(language, 'results.carbon.extraction.ecr'),
            value: pc_ext_ecr,
            percentage: totalDisplayedComponents > 0 ? (pc_ext_ecr / totalDisplayedComponents) * 100 : 0,
          });
        }
      } else {
        // Transformation products
        const totalDisplayedComponents = (pc_extraction || 0) + (pc_trsf || 0) + (pc_trans || 0);

        if (pc_extraction && pc_extraction > 0) {
          phases.push({
            name: getTranslation(language, 'results.carbon.phases.extraction'),
            value: pc_extraction,
            percentage: totalDisplayedComponents > 0 ? (pc_extraction / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (pc_trsf && pc_trsf > 0) {
          phases.push({
            name: getTranslation(language, 'results.carbon.phases.transformation'),
            value: pc_trsf,
            percentage: totalDisplayedComponents > 0 ? (pc_trsf / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (pc_trans && pc_trans > 0) {
          phases.push({
            name: getTranslation(language, 'results.carbon.phases.transportation'),
            value: pc_trans,
            percentage: totalDisplayedComponents > 0 ? (pc_trans / totalDisplayedComponents) * 100 : 0,
          });
        }
      }

      // Build materials list
      const materials = product.materials_used && product.materials_used.length > 0 && product.materials_used.some(m => m.carbon_footprint !== undefined)
        ? product.materials_used
            .filter(m => m.carbon_footprint !== undefined && m.carbon_footprint > 0)
            .map((m) => ({
              name: m.materialName || 'Material',
              quantity: m.quantity,
              unit: m.unit,
              footprint: m.carbon_footprint || 0,
            }))
        : materialConsumptions.map((c) => ({
            name: c.setup_item_name || c.consumption_details?.materialName || 'Material',
            quantity: c.quantity,
            unit: c.unit,
            footprint: c.carbon_footprint || 0,
          }));

      const footprintPerUnit = product.annual_production > 0 
        ? totalCarbon / product.annual_production 
        : 0;
      const footprintPerRevenue = product.product_revenue > 0 
        ? (totalCarbon / product.product_revenue) * 1000 
        : 0;

      carbonData = {
        totalFootprint: totalCarbon,
        activityType,
        phases,
        materials,
        allocation: {
          method: allocationMethod,
          physicalRatio: product.ratio_by_quantity || 0,
          economicRatio: product.ratio_by_revenue || 0,
          decisionRatio,
        },
        efficiency: {
          perUnit: footprintPerUnit,
          perRevenue: footprintPerRevenue,
          unit: product.functional_unit?.unit || 'm3',
        },
      };
    }

    // Calculate hydric consumption breakdowns
    const materialsHydric = consumptions
      .filter((c) => c.consumption_type === "material")
      .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);
    const electricityHydric = consumptions
      .filter((c) => c.consumption_type === "electricity")
      .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);
    const fuelsHydric = consumptions
      .filter((c) => c.consumption_type === "fuels")
      .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);
    const residueHydric = consumptions
      .filter((c) => c.consumption_type === "residue")
      .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);
    const waterHydricConsumption = consumptions
      .filter((c) => c.consumption_type === "water")
      .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

    // Calculate blue and grey water (same as HydricResults)
    const waterConsumptions = consumptions.filter((c) => c.consumption_type === "water");
    const productActivityType = product.product_flow || activityType;
    
    // Separate gray water by activity type
    const extractionGrayWater = waterConsumptions.reduce((sum, c) => {
      if (c.activity_type === "extraction") {
        const grayWater = (c.consumption_details as any)?.ph_agua_cinzenta || 
                         (c.consumption_details as any)?.total_ph_agua_cinzenta || 0;
        return sum + grayWater;
      }
      return sum;
    }, 0);
    
    const transformationGrayWater = waterConsumptions.reduce((sum, c) => {
      if (c.activity_type === "transformation") {
        const grayWater = (c.consumption_details as any)?.ph_agua_cinzenta || 
                         (c.consumption_details as any)?.total_ph_agua_cinzenta || 0;
        return sum + grayWater;
      }
      return sum;
    }, 0);
    
    const relevantGrayWater = productActivityType === "transformation" ? transformationGrayWater : extractionGrayWater;
    const productGrayWaterAllocation = relevantGrayWater * (product.ratio_by_quantity || 0);
    const totalWaterHydricForProduct = (product.hydric_footprint_water || waterHydricConsumption) * (product.annual_production || 0);
    const productBlueWaterAllocation = totalWaterHydricForProduct - productGrayWaterAllocation;

    // Build hydric data
    let hydricData: PdfHydricData | undefined;
    if (link.footprintType === 'hydric' || link.footprintType === 'both') {
      const totalHydric = (product.hydric_footprint_total || 0) * (product.annual_production || 0);
      
      // Get product flow to determine which phases to show
      const productFlow = product.product_flow || activityType;

      // Calculate extraction phase values
      let ph_extraction = undefined;
      let ph_ext_blue = undefined;
      let ph_ext_grey = undefined;
      let ph_ext_materials = undefined;
      let ph_ext_ecr = undefined;

      if (productFlow !== "transformation") {
        // Extraction products
        ph_ext_blue = productBlueWaterAllocation;
        ph_ext_grey = productGrayWaterAllocation;
        ph_ext_materials = (product.hydric_footprint_materials || materialsHydric) * (product.annual_production || 0);
        ph_ext_ecr = (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) * (product.annual_production || 0);
      } else {
        // Transformation products
        if (product.hydric_footprint_extraction && product.hydric_footprint_extraction > 0) {
          ph_extraction = product.hydric_footprint_extraction * (product.annual_production || 0);
        }
      }
      const ph_trans = productFlow !== "extraction"
        ? (product.hydric_footprint_transport || 0) * (product.annual_production || 0)
        : undefined;
      const ph_trsf = productFlow !== "extraction"
        ? ((product.hydric_footprint_materials || materialsHydric) + 
           (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) +
           (product.hydric_footprint_water || waterHydricConsumption)) * (product.annual_production || 0)
        : undefined;

      // Build phases based on product flow
      const phases = [];
      
      if (productFlow === "extraction") {
        const totalDisplayedComponents = (ph_ext_blue || 0) + (ph_ext_grey || 0) + (ph_ext_materials || 0) + (ph_ext_ecr || 0);
        
        if (ph_ext_blue && ph_ext_blue > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.extraction.blue'),
            value: ph_ext_blue,
            percentage: totalDisplayedComponents > 0 ? (ph_ext_blue / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (ph_ext_grey && ph_ext_grey > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.extraction.grey'),
            value: ph_ext_grey,
            percentage: totalDisplayedComponents > 0 ? (ph_ext_grey / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (ph_ext_materials && ph_ext_materials > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.extraction.materials'),
            value: ph_ext_materials,
            percentage: totalDisplayedComponents > 0 ? (ph_ext_materials / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (ph_ext_ecr && ph_ext_ecr > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.extraction.energy'),
            value: ph_ext_ecr,
            percentage: totalDisplayedComponents > 0 ? (ph_ext_ecr / totalDisplayedComponents) * 100 : 0,
          });
        }
      } else {
        // Transformation products
        const totalDisplayedComponents = (ph_extraction || 0) + (ph_trsf || 0) + (ph_trans || 0);

        if (ph_extraction && ph_extraction > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.phases.extraction'),
            value: ph_extraction,
            percentage: totalDisplayedComponents > 0 ? (ph_extraction / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (ph_trsf && ph_trsf > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.phases.transformation'),
            value: ph_trsf,
            percentage: totalDisplayedComponents > 0 ? (ph_trsf / totalDisplayedComponents) * 100 : 0,
          });
        }
        if (ph_trans && ph_trans > 0) {
          phases.push({
            name: getTranslation(language, 'results.hydric.phases.transportation'),
            value: ph_trans,
            percentage: totalDisplayedComponents > 0 ? (ph_trans / totalDisplayedComponents) * 100 : 0,
          });
        }
      }

      // Build materials list
      const materials = product.materials_used && product.materials_used.length > 0 && product.materials_used.some(m => m.hydric_footprint !== undefined)
        ? product.materials_used
            .filter(m => m.hydric_footprint !== undefined && m.hydric_footprint > 0)
            .map((m) => ({
              name: m.materialName || 'Material',
              quantity: m.quantity,
              unit: m.unit,
              footprint: m.hydric_footprint || 0,
            }))
        : materialConsumptions.map((c) => ({
            name: c.setup_item_name || c.consumption_details?.materialName || 'Material',
            quantity: c.quantity,
            unit: c.unit,
            footprint: c.hydric_footprint || 0,
          }));

      const footprintPerUnit = product.annual_production > 0 
        ? totalHydric / product.annual_production 
        : 0;
      const footprintPerRevenue = product.product_revenue > 0 
        ? (totalHydric / product.product_revenue) * 1000 
        : 0;

      hydricData = {
        totalFootprint: totalHydric,
        activityType,
        phases,
        materials,
        allocation: {
          method: 'economic', // Water always uses economic allocation
          physicalRatio: product.ratio_by_quantity || 0,
          economicRatio: product.ratio_by_revenue || 0,
          decisionRatio,
        },
        efficiency: {
          perUnit: footprintPerUnit,
          perRevenue: footprintPerRevenue,
          unit: product.functional_unit?.unit || 'm3',
        },
        blueWater: productBlueWaterAllocation > 0 ? productBlueWaterAllocation : undefined,
        greyWater: productGrayWaterAllocation > 0 ? productGrayWaterAllocation : undefined,
      };
    }

    return {
      companyName: link.companyName,
      companyLogoUrl: company?.logoUrl,
      productName: link.productName,
      year: link.year,
      footprintType: link.footprintType,
      language,
      carbonData,
      hydricData,
      production: {
        quantity: product.annual_production || 0,
        unit: product.functional_unit?.unit || 'm3',
        revenue: product.product_revenue || 0,
      },
      transports: Array.isArray(product.transports) ? product.transports : [],
    };
  }, [footprintDocuments, footprintProducts, footprintConsumptions, companies]);

  const handleExportPdf = async (language: 'en' | 'pt', method: PdfExportMethod) => {
    if (!selectedLinkForPdf) return;

    setPdfError(null);
    setPdfDataLoading(true);

    try {
      const t = (key: string) => getTranslation(language, key);

      // Get company logo
      const company = companies?.find((c) => c.id === selectedLinkForPdf.companyId);

      if (method === 'fullpage') {
        // Use full page capture method
        await fullPagePdfService.exportToPdf(
          selectedLinkForPdf,
          language,
          company?.logoUrl
        );
      } else {
        // Use traditional method
        const pdfData = buildPdfExportData(selectedLinkForPdf, language);

        if (!pdfData) {
          throw new Error('Could not build PDF data. Footprint data may be missing.');
        }

        const pdfTranslations: PdfTranslations = {
          title: t("pdf.title"),
          generatedOn: t("pdf.generatedOn"),
          page: t("pdf.page"),
          of: t("pdf.of"),
          carbonSection: t("pdf.carbonSection"),
          hydricSection: t("pdf.hydricSection"),
          totalCarbonFootprint: t("pdf.totalCarbonFootprint"),
          totalHydricFootprint: t("pdf.totalHydricFootprint"),
          phaseCarbonBreakdown: t("pdf.phaseCarbonBreakdown"),
          phaseHydricBreakdown: t("pdf.phaseHydricBreakdown"),
          materialCarbonBreakdown: t("pdf.materialCarbonBreakdown"),
          materialHydricBreakdown: t("pdf.materialHydricBreakdown"),
          allocationAnalysis: t("pdf.allocationAnalysis"),
          efficiencyMetrics: t("pdf.efficiencyMetrics"),
          productInfo: t("pdf.productInfo"),
          company: t("pdf.company"),
          product: t("pdf.product"),
          year: t("pdf.year"),
          activityType: t("pdf.activityType"),
          extractionActivity: t("pdf.extractionActivity"),
          transformationActivity: t("pdf.transformationActivity"),
          extractionAndTransformationActivity: t("pdf.extractionAndTransformationActivity"),
          allocationMethod: t("pdf.allocationMethod"),
          physicalMethod: t("pdf.physicalMethod"),
          economicMethod: t("pdf.economicMethod"),
          decisionRatio: t("pdf.decisionRatio"),
          physicalRatio: t("pdf.physicalRatio"),
          economicRatio: t("pdf.economicRatio"),
          footprintPerUnit: t("pdf.footprintPerUnit"),
          footprintPerRevenue: t("pdf.footprintPerRevenue"),
          production: t("pdf.production"),
          revenue: t("pdf.revenue"),
          revenuePerUnit: t("pdf.revenuePerUnit"),
          waterAlwaysEconomic: t("pdf.waterAlwaysEconomic"),
          blueWater: t("pdf.blueWater"),
          greyWater: t("pdf.greyWater"),
          noData: t("pdf.noData"),
          tables: {
            phase: t("pdf.tables.phase"),
            value: t("pdf.tables.value"),
            percentage: t("pdf.tables.percentage"),
            material: t("pdf.tables.material"),
            quantity: t("pdf.tables.quantity"),
            unit: t("pdf.tables.unit"),
            footprint: t("pdf.tables.footprint"),
            vehicleType: t("pdf.tables.vehicleType"),
            distance: t("pdf.tables.distance"),
          },
          units: {
            kgCO2e: t("pdf.units.kgCO2e"),
            m3: t("pdf.units.m3"),
          },
          footer: t("pdf.footer"),
          dataResponsibilityTitle: t("pdf.dataResponsibilityTitle"),
          dataResponsibilityContent: t("pdf.dataResponsibilityContent"),
          transportSection: t("pdf.transportSection"),
          transportTotalDistance: t("pdf.transportTotalDistance"),
        };

        await pdfExportService.exportToPdf(pdfData, pdfTranslations);
      }

      toast({
        title: t("results.sharing.success.pdfExported"),
        description: t("results.sharing.descriptions.pdfExported"),
      });

      setIsPdfDialogOpen(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setPdfError(t("results.sharing.error.pdfExportFailed"));
      toast({
        title: t("error"),
        description: t("results.sharing.error.pdfExportFailed"),
        variant: "destructive",
      });
    } finally {
      setPdfDataLoading(false);
    }
  };

  const handleOpenPdfDialog = (link: SharedResultLink) => {
    setSelectedLinkForPdf(link);
    setPdfError(null);
    setIsPdfDialogOpen(true);
  };

  return (
    <>
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Share2 className="h-5 w-5" />
          {t("results.sharing.title")}
        </CardTitle>
        <p className="text-sm text-blue-600">
          {t("results.sharing.description")}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-600">
              {t("results.sharing.shareNote")}
            </p>
          </div>
          <Button
            onClick={generateShareLink}
            disabled={isGenerating}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {t("results.sharing.generating")}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4 mr-2" />
                {t("results.sharing.generateLink")}
              </>
            )}
          </Button>
        </div>

        {/* Shared Links List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-4 w-4 animate-spin text-gray-500" />
            <span className="ml-2 text-sm text-gray-500">{t("results.sharing.loading")}</span>
          </div>
        ) : sharedLinks.length > 0 ? (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
              {t("results.sharing.activeLinks")}
            </h4>
            {sharedLinks.map((link) => (
              <div
                key={link.id}
                className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                                              <Badge
                          variant={link.isActive ? "default" : "secondary"}
                          className={link.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                          }
                        >
                          {link.isActive ? t("results.sharing.status.active") : t("results.sharing.status.inactive")}
                        </Badge>
                      <p className="text-sm font-medium text-gray-700">
                        {link.productName} ({link.year})
                      </p>
                      <span className="text-xs text-gray-500">
                        {t("results.sharing.viewCount")}: {link.viewCount}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openSharedLink(link.shareToken)}
                      className="h-8 w-8 p-0"
                      title={t("results.sharing.openLink")}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(link.shareToken)}
                      className="h-8 w-8 p-0"
                      title={t("results.sharing.copyLink")}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenPdfDialog(link)}
                      className="h-8 w-8 p-0"
                      title={t("results.sharing.exportPdf")}
                    >
                      <FileDown className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleLinkStatus(link.id, link.isActive)}
                      className="h-8 w-8 p-0"
                      title={link.isActive ? t("results.sharing.deactivate") : t("results.sharing.reactivate")}
                    >
                      {link.isActive ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteLink(link.id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      title={t("results.sharing.delete")}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 font-mono break-all bg-gray-50 p-2 rounded">
                    {window.location.origin}/shared/{link.shareToken}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            <Share2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t("results.sharing.noActiveLinks")}</p>
          </div>
        )}

        {/* Access Note */}
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-700">
            {t("results.sharing.accessNote")}
          </p>
        </div>
      </CardContent>
      </Card>

      {/* Export PDF Dialog */}
      <ExportPdfDialog
        isOpen={isPdfDialogOpen}
        onClose={() => {
          setIsPdfDialogOpen(false);
          setSelectedLinkForPdf(null);
          setPdfError(null);
        }}
        onExport={handleExportPdf}
        companyName={selectedLinkForPdf?.companyName || ''}
        productName={selectedLinkForPdf?.productName || ''}
        year={selectedLinkForPdf?.year || new Date().getFullYear()}
        isLoading={pdfDataLoading}
        error={pdfError}
      />
    </>
  );
};
